import React from 'react';

// Preset palettes used across the app. Add new variants here; the component
// stays untouched. Each preset can be overridden per-instance via props.
const VARIANTS = {
  danger: {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    border: '1px solid #7f1d1d',
    shadow: '0 1px 2px rgba(127, 29, 29, 0.35)',
  },
  dangerSoft: {
    color: '#b91c1c',
    background: 'rgba(220, 38, 38, 0.10)',
    border: '1px solid #fca5a5',
    shadow: 'none',
  },
  warning: {
    color: '#7B6F00',
    background: '#FFEB3B',
    border: '1px solid #FBC02D',
    shadow: 'none',
  },
  warningSoft: {
    color: '#b45309',
    background: 'rgba(217, 119, 6, 0.10)',
    border: '1px solid #fcd34d',
    shadow: 'none',
  },
  success: {
    color: '#065f46',
    background: 'rgba(5, 150, 105, 0.10)',
    border: '1px solid #6ee7b7',
    shadow: 'none',
  },
  successSolid: {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    border: '1px solid #047857',
    shadow: '0 1px 2px rgba(4, 120, 87, 0.35)',
  },
  info: {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    border: '1px solid #1d4ed8',
    shadow: '0 1px 2px rgba(29, 78, 216, 0.35)',
  },
  neutral: {
    color: '#374151',
    background: '#f3f4f6',
    border: '1px solid #d1d5db',
    shadow: 'none',
  },
  purple: {
    color: '#ffffff',
    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
    border: '1px solid #5b21b6',
    shadow: '0 1px 2px rgba(91, 33, 182, 0.35)',
  },
};

const SIZES = {
  xs: { fontSize: '0.65rem', padding: '2px 6px', borderRadius: '4px', iconGap: 3 },
  sm: { fontSize: '0.72rem', padding: '3px 8px', borderRadius: '5px', iconGap: 4 },
  md: { fontSize: '0.8rem',  padding: '4px 10px', borderRadius: '6px', iconGap: 5 },
  lg: { fontSize: '0.9rem',  padding: '6px 12px', borderRadius: '8px', iconGap: 6 },
};

/**
 * StatusBadge — reusable pill/badge for statuses, alerts, tags.
 *
 * Props:
 *  - variant: preset name from VARIANTS (default 'neutral')
 *  - size: 'xs' | 'sm' | 'md' | 'lg' (default 'sm')
 *  - icon: emoji or ReactNode rendered before the label
 *  - label: main text (or use children)
 *  - detail: optional secondary text after a middot separator
 *  - title: hover tooltip
 *  - pulse: subtle attention animation for critical badges
 *  - style / className: escape hatches for one-off overrides
 */
export default function StatusBadge({
  variant = 'neutral',
  size = 'sm',
  icon,
  label,
  detail,
  title,
  pulse = false,
  style,
  className,
  children,
  ...rest
}) {
  const v = VARIANTS[variant] || VARIANTS.neutral;
  const s = SIZES[size] || SIZES.sm;

  return (
    <span
      title={title}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: s.iconGap,
        color: v.color,
        background: v.background,
        border: v.border,
        boxShadow: v.shadow,
        fontWeight: 700,
        fontSize: s.fontSize,
        lineHeight: 1.2,
        padding: s.padding,
        borderRadius: s.borderRadius,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        maxWidth: '100%',
        letterSpacing: '0.02em',
        animation: pulse ? 'statusBadgePulse 1.8s ease-in-out infinite' : undefined,
        ...style,
      }}
      {...rest}
    >
      {icon != null && <span style={{ fontSize: '0.95em', lineHeight: 1 }}>{icon}</span>}
      {label != null && <span>{label}</span>}
      {detail != null && (
        <>
          <span style={{ opacity: 0.55, fontWeight: 500 }}>·</span>
          <span style={{ fontWeight: 600, opacity: 0.95 }}>{detail}</span>
        </>
      )}
      {children}
    </span>
  );
}

// Keyframes injected once for the optional pulse animation.
if (typeof document !== 'undefined' && !document.getElementById('status-badge-keyframes')) {
  const style = document.createElement('style');
  style.id = 'status-badge-keyframes';
  style.textContent = `
    @keyframes statusBadgePulse {
      0%, 100% { transform: scale(1); filter: brightness(1); }
      50%      { transform: scale(1.04); filter: brightness(1.08); }
    }
  `;
  document.head.appendChild(style);
}

export { VARIANTS as BADGE_VARIANTS, SIZES as BADGE_SIZES };
